<script setup lang="ts">
  import type { MatrixCodeRevision } from '@/entities/Matrix';
  import NoFilesIcon from '@/shared/assets/images/no-files.svg';
  import { DefaultTable, LoadErrorState, NoDocuments, TableHeader, TableLoading } from '@/shared/ui';
  import {
    MATRIX_REVISIONS_EMPTY_TITLE,
    MATRIX_REVISIONS_LOAD_ERROR_TITLE,
  } from '../../config';
  import MatrixRevisionsTableRow from './MatrixRevisionsTableRow.vue';

  const props = defineProps<{
    rows: MatrixCodeRevision[];
    loading: boolean;
    error: boolean;
  }>();

  const emit = defineEmits<{
    retry: [];
  }>();

  defineSlots<{
    header(): unknown;
    actions(props: { row: MatrixCodeRevision }): unknown;
  }>();

  const HEADERS = ['Registro', 'Título', 'Rev.', 'Status', 'Ações'];

  const COLUMN_WIDTHS = ['w-36', '', 'w-20', 'w-32', 'w-44'];
</script>

<template>
  <DefaultTable
    class="divide-y divide-gray-02"
    table-class="w-full table-fixed"
  >
    <colgroup>
      <col
        v-for="(width, index) in COLUMN_WIDTHS"
        :key="HEADERS[index]"
        :class="width"
      >
    </colgroup>
    <thead class="bg-white-01 divide-y divide-gray-02">
      <tr>
        <td
          :colspan="HEADERS.length"
          class="p-3"
        >
          <slot name="header" />
        </td>
      </tr>
      <tr>
        <TableHeader
          v-for="header in HEADERS"
          :key="header"
          :text="header"
          class="text-xs/5"
        />
      </tr>
    </thead>
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="props.loading">
        <td :colspan="HEADERS.length">
          <TableLoading
            data-testid="matrix-revisions-loading"
            :row-size="5"
            :column-size="HEADERS.length"
          />
        </td>
      </tr>
      <tr
        v-else-if="props.error"
        data-testid="matrix-revisions-error"
      >
        <td
          :colspan="HEADERS.length"
          class="py-8"
        >
          <LoadErrorState
            :title="MATRIX_REVISIONS_LOAD_ERROR_TITLE"
            @retry="emit('retry')"
          />
        </td>
      </tr>
      <tr
        v-else-if="props.rows.length === 0"
        data-testid="matrix-revisions-empty"
      >
        <td
          :colspan="HEADERS.length"
          class="py-8"
        >
          <NoDocuments
            :title="MATRIX_REVISIONS_EMPTY_TITLE"
            :icon="NoFilesIcon"
          />
        </td>
      </tr>
      <MatrixRevisionsTableRow
        v-for="row in props.rows"
        v-else
        :key="row.id"
        :row="row"
      >
        <template #actions="{ row: actionRow }">
          <slot
            name="actions"
            :row="actionRow"
          />
        </template>
      </MatrixRevisionsTableRow>
    </tbody>
  </DefaultTable>
</template>
